import React, { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { PriorityBadge } from "@/components/badges";
import { CoveragePanel } from "@/components/CoveragePanel";
import { Search, LayoutDashboard, List } from "lucide-react";

export default function CallTypes() {
  const [view, setView] = useState("catalog");
  const [disc, setDisc] = useState("police");
  const [q, setQ] = useState("");
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (q.trim().length >= 2) {
      const { data } = await client.get("/call-types-smart-search", { params: { q: q.trim(), discipline: disc, limit: 100 } });
      setTypes(data.results.filter((t) => t.discipline === disc || t.suggested));
    } else {
      const { data } = await client.get("/call-types", { params: { discipline: disc, limit: 500 } });
      setTypes(data);
    }
  }, [disc, q]);
  useEffect(() => { client.get("/call-types-stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <Layout>
      <PageHeader title="Call-for-Service Catalog" sub={stats ? `Police ${stats.police} · Fire ${stats.fire} · EMS ${stats.ems} — coded call types` : "Loading…"}
        right={
          <div className="flex gap-1">
            <button data-testid="view-catalog-btn" onClick={() => setView("catalog")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-head border ${view === "catalog" ? "bg-[#3B82F6] border-[#3B82F6] text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}>
              <List size={13} /> Catalog</button>
            <button data-testid="view-coverage-btn" onClick={() => setView("coverage")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-head border ${view === "coverage" ? "bg-[#10B981] border-[#10B981] text-black" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}>
              <LayoutDashboard size={13} /> Coverage</button>
          </div>
        } />
      {view === "coverage" ? (
        <div className="flex-1 overflow-y-auto"><CoveragePanel /></div>
      ) : (
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="p-3 border-b border-[#2C2D35] bg-[#0d0e11] flex items-center gap-2">
          {["police", "fire", "ems"].map((d) => (
            <button key={d} data-testid={`catalog-tab-${d}`} onClick={() => setDisc(d)}
              className={`px-3 py-1.5 text-xs font-head border transition-colors ${disc === d ? "border-transparent text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}
              style={disc === d ? { background: d === "police" ? "#3B82F6" : d === "fire" ? "#EF4444" : "#10B981" } : {}}>
              {d.toUpperCase()}
            </button>
          ))}
          <div className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-2 ml-auto w-72">
            <Search size={13} className="text-[#9CA3AF]" />
            <input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search call types…" className="flex-1 bg-transparent py-1.5 text-xs outline-none font-mono" />
          </div>
          <span className="font-mono text-[11px] text-[#9CA3AF]">{types.length} shown</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-left">
            <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11] sticky top-0 z-10">
              <tr><th className="px-3 py-2">Code</th><th className="px-2">Pri</th><th className="px-2">Type</th><th className="px-2">Category</th><th className="px-2 w-32"></th></tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <React.Fragment key={t.code}>
                  <tr data-testid={`catalog-row-${t.code}`} className="border-b border-[#2C2D35] hover:bg-[#141518]">
                    <td className="px-3 py-1.5 font-mono text-[11px]">{t.code}</td>
                    <td className="px-2"><PriorityBadge priority={t.priority} /></td>
                    <td className="px-2 text-sm font-head">{t.label}</td>
                    <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{t.category}</td>
                    <td className="px-2">
                      <button data-testid={`catalog-expand-${t.code}`} onClick={async () => {
                        if (open === t.code) { setOpen(null); return; }
                        if (!t.questions) {
                          try { const { data } = await client.get(`/call-types/${t.code}`);
                            setTypes((prev) => prev.map((x) => x.code === t.code ? { ...x, ...data } : x)); } catch { /* ignore */ }
                        }
                        setOpen(t.code);
                      }}
                        className="text-[11px] font-head text-[#3B82F6] hover:underline">{open === t.code ? "Hide" : "Protocol"}</button>
                    </td>
                  </tr>
                  {open === t.code && (
                    <tr className="border-b border-[#2C2D35] bg-[#0d0e11]">
                      <td colSpan={5} className="px-6 py-3">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <div className="font-mono text-[10px] text-[#3B82F6] uppercase mb-1">Interrogation</div>
                            <ul className="text-[11px] space-y-1">{(t.questions || []).map((x, i) => <li key={i}>• {x}</li>)}</ul>
                          </div>
                          <div>
                            <div className="font-mono text-[10px] text-[#10B981] uppercase mb-1">Pre-Arrival</div>
                            <ul className="text-[11px] space-y-1">{(t.pre_arrival || []).map((x, i) => <li key={i}>• {x}</li>)}</ul>
                          </div>
                          <div>
                            <div className="font-mono text-[10px] text-[#F59E0B] uppercase mb-1">Dispatch</div>
                            <div className="text-[11px] font-mono">{t.dispatch}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </Layout>
  );
}
